/*
 * Treat — option controls on a product card.
 *
 * Resolves a variant from the CURRENT COMBINATION of option values, so a card can
 * carry a swatch row and a pill row at once. The previous version mapped one button
 * to one variant, which only works for single-option products.
 *
 * On selection it updates: the hidden variant id, the price and sale badge, the Add-to-bag state,
 * both card links' ?variant= (title and image), the card image (variant featured media, else
 * what the card rendered with) and the hover image (resolved per variant in the JSON; hidden
 * when that variant has none).
 *
 * Variant data comes from a JSON script tag the card emits once, rather than data
 * attributes per control — smaller markup and one parse per card.
 *
 * Legacy support: cards still using the old one-button-per-variant markup
 * (.card-variant-pills__option[data-variant-id]) keep working.
 */
if (!customElements.get('treat-card-variant-pills')) {
  customElements.define(
    'treat-card-variant-pills',
    class TreatCardVariantPills extends HTMLElement {
      connectedCallback() {
        /*
         * Scope is the CARD, not the closest form — ported from Moonglaze's
         * ProductCard.syncSelectedVariantToForms (base.js:3848-3870), which collects every
         * `form input[name="id"]` under the card rather than one input under one form.
         *
         * Treat renders a single form per card today, verified across all four sections that
         * pass swatch parameters (featured-collection, main-collection-product-grid, main-search,
         * related-products; collage renders the card with no option controls at all). So this is
         * not fixing a live miscount — it removes the assumption. A second form on the card, from
         * a quick view or an app block, would otherwise keep submitting the first available
         * variant while the customer looked at another one.
         *
         * The old code also bailed out entirely when there was no ancestor form, which would have
         * taken the swatches down with it — the controls do more than feed the form, they rewrite
         * the card link's ?variant=. Scoping to the card makes the form genuinely optional.
         */
        const cardRoot = this.closest('.card-wrapper') || this;
        this.variantIdInputs = Array.from(cardRoot.querySelectorAll('form input[name="id"]'));
        this.submitButton = cardRoot.querySelector('form [type="submit"]');
        this.submitButtonLabel = this.submitButton?.querySelector('span:not(.sold-out-message)');
        this.soldOutMessage = this.submitButton?.querySelector('.sold-out-message');

        this.priceElement = cardRoot.querySelector('[data-treat-card-price]');
        this.saleBadgeElement = cardRoot.querySelector('[data-treat-card-sale-badge]');
        this.imageElement = cardRoot.querySelector('[data-treat-card-image]');
        this.hoverImageElement = cardRoot.querySelector('[data-treat-card-hover-image]');
        // The title link and the image link (the only two click targets on the card).
        this.linkElements = Array.from(cardRoot.querySelectorAll('[data-treat-card-link]'));

        this.fallbackImage = this.readImageAttributes(this.imageElement);

        this.variants = this.readVariants();
        this.optionButtons = Array.from(this.querySelectorAll('[data-option-value]'));
        this.legacyButtons = Array.from(this.querySelectorAll('.card-variant-pills__option[data-variant-id]'));

        this.onClick = this.onClick.bind(this);
        this.optionButtons.concat(this.legacyButtons).forEach((button) => {
          button.addEventListener('click', this.onClick);
        });

        if (this.optionButtons.length) {
          this.selected = this.readInitialSelection();
          this.apply();
        } else if (this.legacyButtons.length) {
          const preselected =
            this.querySelector('.card-variant-pills__option.is-selected:not([disabled])') ||
            this.querySelector('.card-variant-pills__option:not([disabled])');
          if (preselected) this.applyLegacy(preselected);
        }
      }

      disconnectedCallback() {
        this.optionButtons?.concat(this.legacyButtons || []).forEach((button) => {
          button.removeEventListener('click', this.onClick);
        });
      }

      readImageAttributes(element) {
        if (!element) return null;
        return { src: element.getAttribute('src'), srcset: element.getAttribute('srcset') };
      }

      readVariants() {
        const node = this.querySelector('[data-treat-card-variants]');
        if (!node) return [];
        try {
          return JSON.parse(node.textContent) || [];
        } catch {
          return [];
        }
      }

      readInitialSelection() {
        const selected = [];
        this.optionButtons.forEach((button) => {
          const index = parseInt(button.dataset.optionPosition, 10) - 1;
          if (Number.isNaN(index)) return;
          if (selected[index] === undefined || button.classList.contains('is-selected')) {
            selected[index] = button.dataset.optionValue;
          }
        });
        return selected;
      }

      onClick(event) {
        const button = event.currentTarget;
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        if (button.dataset.variantId && !button.dataset.optionValue) {
          this.applyLegacy(button);
          return;
        }

        const index = parseInt(button.dataset.optionPosition, 10) - 1;
        if (Number.isNaN(index)) return;
        this.selected[index] = button.dataset.optionValue;
        this.apply();
      }

      matchVariant() {
        return this.variants.find((variant) =>
          this.selected.every((value, index) => value === undefined || variant.options[index] === value)
        );
      }

      apply() {
        this.optionButtons.forEach((button) => {
          const index = parseInt(button.dataset.optionPosition, 10) - 1;
          const isSelected = this.selected[index] === button.dataset.optionValue;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });

        // No variant data at all (unreadable JSON) says nothing about stock: leave the button as the
        // server rendered it instead of disabling every card as "Sold out".
        if (!this.variants.length) return;

        const variant = this.matchVariant();
        if (!variant) {
          this.setAddToBagEnabled(false);
          return;
        }

        this.syncVariantToForms(variant.id, variant.available);
        if (this.priceElement && variant.price) {
          // The price HTML carries the struck-through compare-at (or promo) price of THIS variant,
          // rendered by snippets/treat-card-price.liquid, so price and discount never mix variants.
          this.priceElement.innerHTML = variant.price;
        }
        if (this.saleBadgeElement && typeof variant.badge === 'string') {
          this.saleBadgeElement.innerHTML = variant.badge;
          this.saleBadgeElement.hidden = variant.badge === '';
        }
        this.linkElements.forEach((link) => {
          const base = link.dataset.baseUrl || link.getAttribute('href').split('?')[0];
          link.setAttribute('href', `${base}?variant=${variant.id}`);
        });
        this.swapImage(variant);
        this.setAddToBagEnabled(variant.available);
      }

      /*
       * Both image layers are rebound together, and that is the whole point.
       *
       * The hover layer sits AFTER the primary in DOM order, and base.css:1133 gives every .media
       * child inset:0 absolute positioning — so the hover layer paints on top. Its opacity is 1
       * whenever the card is hovered, which it always is at the moment a swatch is clicked, because
       * the pointer is on the card. Rebinding only the primary therefore swapped an image the
       * customer could not see, underneath a stale one they could. Rebind both, or neither works.
       */
      swapImage(variant) {
        this.applyImage(this.imageElement, variant.image, variant.srcset, this.fallbackImage, variant.alt);
        this.applyHoverImage(variant);
      }

      /*
       * The hover source is already resolved per variant in the card's JSON (the variant's own
       * Hover image, else the product's first image that belongs to no variant). No fallback to
       * what the card rendered with: that was the FIRST variant's hover art, so a variant without
       * its own showed another variant's picture. A variant with nothing to show hides the layer.
       */
      applyHoverImage(variant) {
        const element = this.hoverImageElement;
        if (!element) return;
        if (!variant.hover) {
          element.hidden = true;
          return;
        }
        if (element.getAttribute('src') !== variant.hover) {
          element.setAttribute('src', variant.hover);
          if (variant.hoverSrcset) {
            element.setAttribute('srcset', variant.hoverSrcset);
          } else {
            element.removeAttribute('srcset');
          }
        }
        element.hidden = false;
      }

      // A variant with no image of its own falls back to whatever the card rendered with, so a
      // partially-configured product degrades to the old behaviour rather than to a blank frame.
      //
      // src and srcset fall back together, never independently. Taking a variant's src while
      // keeping the fallback's srcset would let the browser pick a descriptor that resolves to a
      // DIFFERENT product image at some viewport widths — the previous `variant.srcset ||
      // fallback.srcset` did exactly that whenever a variant had an image but no srcset.
      applyImage(element, src, srcset, fallback, alt) {
        if (!element) return;
        const nextSrc = src || fallback?.src;
        const nextSrcset = src ? srcset : fallback?.srcset;
        if (!nextSrc) return;
        if (element.getAttribute('src') === nextSrc) return;

        element.setAttribute('src', nextSrc);
        if (nextSrcset) {
          element.setAttribute('srcset', nextSrcset);
        } else {
          element.removeAttribute('srcset');
        }
        if (alt) element.setAttribute('alt', alt);
      }

      applyLegacy(button) {
        this.legacyButtons.forEach((other) => {
          const isSelected = other === button;
          other.classList.toggle('is-selected', isSelected);
          other.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });
        this.syncVariantToForms(button.dataset.variantId, button.dataset.variantAvailable === 'true');
        if (this.priceElement && button.dataset.variantPrice) {
          // innerHTML, not textContent: the store's money format may wrap prices in
          // <span class=money> for the BUCKS currency converter, and text would print the tags.
          this.priceElement.innerHTML = button.dataset.variantPrice;
        }
        this.setAddToBagEnabled(button.dataset.variantAvailable === 'true');
      }

      // Points every product form on the card at the chosen variant. Disabling the input as well
      // as the button is Sense's belt-and-braces: a disabled input is not submitted, so an
      // unavailable variant cannot be added even if the button is reached another way.
      syncVariantToForms(variantId, isAvailable) {
        this.variantIdInputs.forEach((input) => {
          input.value = variantId;
          input.disabled = !isAvailable;
        });
      }

      setAddToBagEnabled(isEnabled) {
        if (!this.submitButton) return;
        if (isEnabled) {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtonLabel?.classList.remove('hidden');
          this.soldOutMessage?.classList.add('hidden');
        } else {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (this.soldOutMessage && this.submitButtonLabel) {
            this.submitButtonLabel.classList.add('hidden');
            this.soldOutMessage.classList.remove('hidden');
          }
        }
      }
    }
  );
}

/* Second image on touch (2026-10-11, revised). Phones have no hover, so the FIRST tap on a
   card image shows the second image and keeps it; a second tap on the same image opens the
   product. Tapping anywhere else, or any scroll (page or slider), brings the first image back.
   Same idea as Moonglaze's card, made explicit so iOS and Android behave the same.
   Delegated once on document, so every card everywhere (home, collections, search,
   Pairs well with) gets it, including cards added later. */
if (!window.treatCardTouchReveal) {
  window.treatCardTouchReveal = true;

  const touchOnly = window.matchMedia('(hover: none)');
  let revealedBox = null;

  const hide = () => {
    if (!revealedBox) return;
    revealedBox.classList.remove('is-touch-reveal');
    revealedBox = null;
  };

  const boxWithSecondImage = (target) => {
    const box = target.closest && target.closest('.treat-card .card__inner');
    if (!box) return null;
    const secondary = box.querySelector('.treat-card__media-secondary');
    return secondary && !secondary.hidden && secondary.getAttribute('src') ? box : null;
  };

  document.addEventListener(
    'click',
    (event) => {
      if (!touchOnly.matches) return;
      const box = boxWithSecondImage(event.target);
      if (!box || !event.target.closest('.treat-card__media-link, .card__media')) return;
      if (box === revealedBox) return; /* second tap: open the product */
      event.preventDefault();
      event.stopPropagation();
      hide();
      revealedBox = box;
      box.classList.add('is-touch-reveal');
    },
    true
  );

  document.addEventListener(
    'touchstart',
    (event) => {
      if (revealedBox && !revealedBox.contains(event.target)) hide();
    },
    { passive: true }
  );

  document.addEventListener('scroll', hide, { capture: true, passive: true });
}
